import { ResultElementStateLabels } from "shared/labels";
import { cellModelParametersGroupName, cellModelParameterUnitsType, findDictionaryItemByValue, N_A, } from "shared/rsDictionary";
import { formatCellModelParameterValue, getSelectedCellModelParameterToOptimize, } from "store/cell-model/cell-model.utils";
import { RecipeStepType, } from "store/recipe/recipe.types";
import { checkRecipeParameterIsCompleted, flatSteps, getAlternativeStepFlowName, getFlowNameById, getSelectedRecipeAlternativeStepToOptimize, getSelectedRecipeParameterToOptimize, getUnitsCategory, } from "store/recipe/recipe.utils";
import { convertValueBasedOnUnits } from "utils/final-form-mutators";
import { isNotEmpty } from "utils/validate";
import { InsightInputType, OptimizationParameterParameterType, OptimizationTargetType, ResultElementParameterType, ResultElementSourceType, ResultSetElementType, SimulationRunState, } from "../rs-workspace.types";
import { mapSpaceViewerDataToSourceTemplate, MAX_RESULT_ELEMENTS } from "./results.utils";
export const MIN_FLOWS_QUANTITY = 2;
export const getEmptySpaceviewerResultSource = (sourceId) => ({
    sourceId: sourceId ?? 0,
    elementType: ResultSetElementType.SpaceViewer,
    state: SimulationRunState.UNKNOWN,
    parameterIdsSequence: [],
    fetching: false,
    accuracy: null,
});
export const getUsedTargetsFromSource = (source, cellModels, recipes, simulations, insights) => {
    const usedRecipes = [];
    const usedCellModels = [];
    const usedSimulations = [];
    switch (source?.type) {
        case ResultElementSourceType.Simulation:
            {
                const { id: sourceId } = (source ?? {});
                const usedSimulation = simulations.find(({ id }) => id === sourceId || 0);
                const { recipeId, cellModelId } = (usedSimulation || {});
                usedCellModels.push(cellModels.find(({ id }) => id === cellModelId));
                usedRecipes.push(recipes.find(({ id }) => id === recipeId));
                usedSimulations.push(usedSimulation);
            }
            break;
        case ResultElementSourceType.Insight:
            {
                const { insightId, inputSetId } = (source ?? {});
                const insight = insights.find(({ id }) => id === insightId);
                const inputSet = insight?.state.input.inputSets.find(({ id }) => id === inputSetId);
                const simulationsIds = inputSet?.inputs.map(({ inputId }) => inputId);
                simulationsIds?.forEach((simulationId) => {
                    const usedSimulation = simulations.find(({ id }) => id === simulationId);
                    const { recipeId, cellModelId } = (usedSimulation || {});
                    usedCellModels.push(cellModels.find(({ id }) => id === cellModelId));
                    usedRecipes.push(recipes.find(({ id }) => id === recipeId));
                    usedSimulations.push(usedSimulation);
                });
            }
            break;
        default:
            break;
    }
    return { usedCellModels, usedRecipes, usedSimulations };
};
export const getRecipeParameterValues = (recipes, parameterName) => recipes.reduce((acc, { state: { parameters }, id, name }) => [
    ...acc,
    ...parameters
        .filter(({ name }) => name === parameterName)
        .map(({ value, units }) => ({
        targetId: id,
        value: isNotEmpty(value) ? value : N_A,
        units,
        targetName: name,
        highlighted: false,
    })),
], []);
export const getCellModelParameterValues = (cellModels, parameterName) => cellModels.reduce((acc, { state: { parameters }, id, name }) => [
    ...acc,
    ...Object.values(parameters)
        .map((parametersList) => parametersList)
        .flat()
        .filter((parameter) => parameter.name === parameterName)
        .map(({ parameterValue, units, isComplete }) => ({
        targetId: id,
        value: isComplete ? formatCellModelParameterValue(parameterValue) : N_A,
        units,
        targetName: name,
        highlighted: false,
    })),
], []);
export const getRecipeAlternativeStepFlows = (recipes, stepName) => recipes.reduce((acc, { state: { steps }, id, name }) => {
    flatSteps(steps).forEach((step) => {
        if (step.type === RecipeStepType.ALTERNATIVE && step.name === stepName) {
            [...step.flowProperties].forEach((flow, index) => {
                const value = getAlternativeStepFlowName(flow, index);
                const existedIndex = acc.findIndex((instance) => instance.value === value);
                if (existedIndex === -1) {
                    acc.push({
                        targetId: id,
                        value,
                        targetName: name,
                        highlighted: false,
                    });
                }
                else {
                    acc[existedIndex].targetName += `, ${name}`;
                }
            });
        }
    });
    return acc;
}, []);
export const getResultElementRecipeParameterItemOptionType = (item) => ({
    value: item,
    label: `${item.name}${item.recipeNames.length > 0 ? ` (${item.recipeNames.join(", ")})` : ""}`,
    disabled: !item.isCompleted,
});
export const getResultElementRecipeParameterAndAlternativeStepsItems = (availableRecipes, selectedParameters, parameterTypes, currentParameter, byUnits) => {
    const { usedParameters, usedAlternativeSteps } = selectedParameters.reduce((acc, parameter) => {
        if (parameter?.type === ResultElementParameterType.RECIPE) {
            acc.usedParameters.push(parameter);
        }
        if (parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW) {
            acc.usedAlternativeSteps.push(parameter);
        }
        return acc;
    }, { usedParameters: [], usedAlternativeSteps: [] });
    const parameterItems = [];
    const alternativeStepItems = [];
    availableRecipes.forEach((recipe) => {
        recipe.state.parameters.forEach((parameter) => {
            const { name, units, id } = parameter;
            const isParameterCompleted = checkRecipeParameterIsCompleted(parameter);
            const { category: unitsCategory, variablesList = [] } = { ...getUnitsCategory(parameterTypes, units) };
            let parameterShouldBeAdded = false;
            if (byUnits) {
                const usedParameterUnits = usedParameters
                    .filter(({ name: usedName, units: usedUnits }) => usedName === name &&
                    usedUnits &&
                    getUnitsCategory(parameterTypes, usedUnits)?.category === unitsCategory)
                    .map(({ units }) => units);
                if (!usedParameterUnits.length || !unitsCategory) {
                    parameterShouldBeAdded = true;
                }
                else {
                    if (!variablesList.every(({ type }) => usedParameterUnits.includes(type))) {
                        parameterShouldBeAdded = true;
                    }
                }
            }
            else {
                if (!usedParameters.find((parameter) => name === parameter.name)) {
                    parameterShouldBeAdded = true;
                }
            }
            if (parameterShouldBeAdded && name && units && unitsCategory) {
                const existedItem = parameterItems.find(({ name: existedName, unitsCategory: existedCategory }) => existedName === name && existedCategory === unitsCategory);
                if (existedItem) {
                    existedItem.recipeNames.push(recipe.name);
                    existedItem.selectedUnits.push(units);
                    if (!existedItem.isCompleted && isParameterCompleted) {
                        existedItem.isCompleted = isParameterCompleted;
                    }
                }
                else {
                    parameterItems.push({
                        id,
                        isAlternativeStep: false,
                        recipeId: recipe.id,
                        name,
                        isCompleted: isParameterCompleted,
                        unitsCategory: unitsCategory,
                        recipeNames: [recipe.name],
                        selectedUnits: [units],
                    });
                }
            }
        });
        flatSteps(recipe.state.steps)
            .filter((step) => step.type === RecipeStepType.ALTERNATIVE && step.flowProperties.length >= MIN_FLOWS_QUANTITY)
            .forEach((step) => {
            const { id, isCompleted, name = "" } = step;
            if (!usedAlternativeSteps.find((parameter) => parameter.name === name)) {
                const existedItem = alternativeStepItems.find(({ name: existedName }) => existedName === name);
                if (existedItem) {
                    existedItem.recipeNames.push(recipe.name);
                    if (!existedItem.isCompleted && isCompleted) {
                        existedItem.isCompleted = isCompleted;
                    }
                }
                else {
                    alternativeStepItems.push({
                        stepId: id,
                        name,
                        isAlternativeStep: true,
                        recipeNames: [recipe.name],
                        isCompleted,
                    });
                }
            }
        });
    });
    if (isNotEmpty(currentParameter?.name, true)) {
        if (currentParameter?.type === ResultElementParameterType.RECIPE &&
            !parameterItems.find(({ name }) => name === currentParameter?.name)) {
            const { category: unitsCategory = "" } = {
                ...getUnitsCategory(parameterTypes, currentParameter?.units),
            };
            parameterItems.push({
                name: currentParameter?.name ?? "",
                isCompleted: false,
                unitsCategory,
                isAlternativeStep: false,
                recipeNames: [],
                selectedUnits: [],
            });
        }
        if (currentParameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW &&
            !alternativeStepItems.find(({ name }) => name === currentParameter?.name)) {
            alternativeStepItems.push({
                name: currentParameter?.name ?? "",
                isCompleted: false,
                isAlternativeStep: true,
                recipeNames: [],
            });
        }
    }
    return getRecipeParametersAndAlternativeStepsOptions(parameterItems
        .sort(({ name: a }, { name: b }) => a.localeCompare(b))
        .map(getResultElementRecipeParameterItemOptionType), alternativeStepItems
        .sort(({ name: a }, { name: b }) => a.localeCompare(b))
        .map(getResultElementRecipeParameterItemOptionType));
};
export const getRecipeParametersAndAlternativeStepsOptions = (parameters, alternativeSteps) => {
    const groups = ["alt_steps", "params"];
    const stepsGroupName = {
        value: "Group of alternative flows",
        label: "Group of alternative flows",
        isGroupName: true,
        disabled: true,
        groupId: groups[0],
    };
    const parametersGroupName = {
        value: "Recipe Parameters",
        label: "Recipe Parameters",
        isGroupName: true,
        disabled: true,
        groupId: groups[1],
    };
    return [
        ...(alternativeSteps.length
            ? [stepsGroupName, ...alternativeSteps.map((option) => ({ ...option, groupId: groups[0] }))]
            : []),
        ...(parameters.length
            ? [parametersGroupName, ...parameters.map((option) => ({ ...option, groupId: groups[1] }))]
            : []),
    ];
};
export const getSelectedResultElementRecipeParameter = (recipes, parameterTypes, parameter) => {
    const { type } = parameter;
    if (!type) {
        return;
    }
    const items = getResultElementRecipeParameterAndAlternativeStepsItems(recipes, [], parameterTypes, parameter);
    if (type === ResultElementParameterType.ALTERNATIVE_FLOW) {
        return items.find(({ value }) => value.name === parameter.name);
    }
    else {
        const { category: unitsCategory } = {
            ...getUnitsCategory(parameterTypes, parameter.units),
        };
        return items.find(({ value }) => value.name === parameter.name &&
            value.unitsCategory === unitsCategory);
    }
};
export const getResultElementCellModelParameterItemOptionType = (item) => ({
    value: item,
    label: `${item.name}${item.cellModelNames.length > 0 ? ` (${item.cellModelNames.join(", ")})` : ""}`,
    disabled: !item.isCompleted,
});
export const getResultElementCellModelsParameterItems = (cellModels, currentParameter) => {
    const parametersByGroup = cellModels.reduce((acc, { id: cellModelId, state: { parameters }, name: cellModelName }) => {
        Object.keys(parameters ?? {}).forEach((key) => {
            parameters[key].forEach(({ id, name, units, parameterValue }) => {
                const isParameterCompleted = isNotEmpty(parameterValue);
                const existedGroup = acc[key];
                if (!existedGroup) {
                    acc[key] = [
                        {
                            id,
                            cellModelId,
                            name,
                            units,
                            cellModelNames: [cellModelName],
                            isCompleted: isParameterCompleted,
                        },
                    ];
                }
                else {
                    const existedItem = acc[key].find((item) => item.name === name && item.units === units);
                    if (existedItem) {
                        existedItem.cellModelNames.push(cellModelName);
                        if (!existedItem.isCompleted && isParameterCompleted) {
                            existedItem.isCompleted = true;
                        }
                    }
                    else {
                        acc[key].push({
                            id,
                            cellModelId,
                            name,
                            units,
                            cellModelNames: [cellModelName],
                            isCompleted: isParameterCompleted,
                        });
                    }
                }
            });
        });
        return acc;
    }, {});
    const parameters = Object.keys(parametersByGroup).reduce((acc, key) => {
        const groupNameItem = {
            value: key,
            label: cellModelParametersGroupName[key],
            isGroupName: true,
            groupId: key,
            disabled: true,
        };
        const items = parametersByGroup[key].map((item) => ({ ...getResultElementCellModelParameterItemOptionType(item), groupId: key }), []);
        return [...acc, groupNameItem, ...items];
    }, []);
    if (currentParameter.name && !parameters.find(({ value }) => value.name === currentParameter.name)) {
        parameters.push(getResultElementCellModelParameterItemOptionType({
            name: currentParameter.name ?? "",
            isCompleted: false,
            units: currentParameter.units,
            cellModelNames: [],
        }));
    }
    return parameters;
};
export const getSelectedResultElementCellModelParameter = (cellModels, parameter) => getResultElementCellModelsParameterItems(cellModels, parameter)
    .filter(({ isGroupName }) => !isGroupName)
    .find(({ value: { name, units } }) => name === parameter?.name && units === parameter?.units);
export const getCombinationParameterName = (parameter, recipes, cellModels, parameterTypes) => {
    if (parameter.parameter === undefined || parameter.parameter === null) {
        return "";
    }
    const selectedParameterItem = parameter?.parameter?.type === ResultElementParameterType.CELL_MODEL
        ? getSelectedResultElementCellModelParameter(cellModels, parameter.parameter)
        : getSelectedResultElementRecipeParameter(recipes, parameterTypes, parameter.parameter);
    return selectedParameterItem?.label;
};
export const getCombinationParameterPreview = (parameter, recipes, cellModels, parameterTypes) => {
    const parameterParameter = { ...(parameter?.parameter ?? {}) };
    switch (parameter.parameter?.type) {
        case ResultElementParameterType.CELL_MODEL: {
            parameterParameter.name =
                getSelectedResultElementCellModelParameter(cellModels, parameter.parameter)?.label ?? "";
            parameterParameter.units = findDictionaryItemByValue(cellModelParameterUnitsType, parameter.parameter.units)?.label;
            break;
        }
        case ResultElementParameterType.RECIPE: {
            parameterParameter.name =
                getSelectedResultElementRecipeParameter(recipes, parameterTypes, parameter.parameter)?.label ?? "";
            parameterParameter.units = findDictionaryItemByValue(cellModelParameterUnitsType, parameter.parameter.units)?.label;
            break;
        }
        case ResultElementParameterType.ALTERNATIVE_FLOW: {
            parameterParameter.name =
                getSelectedResultElementRecipeParameter(recipes, parameterTypes, parameter.parameter)?.label ?? "";
            parameterParameter.type = ResultElementParameterType.RECIPE;
            break;
        }
    }
    return {
        ...parameter,
        parameter: parameterParameter,
    };
};
const getEmptyParameterInstance = () => ({
    targetId: 0,
    targetName: ResultElementStateLabels.PARAMETER_IS_N_A,
    highlighted: false,
    value: N_A,
});
const getParameterInstanceFromSimulation = (parameter, parameterInstances, simulations, cellModels, recipes, simulationId, adjustmentId) => {
    const { name, type } = parameter?.parameter ?? {};
    const simulation = simulations.find((simulationItem) => simulationItem.id === simulationId) || {};
    const { recipeId, cellModelId, parametersForVariants } = simulation;
    const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
    switch (type) {
        case ResultElementParameterType.CELL_MODEL: {
            const cellModel = cellModels.find(({ id }) => id === cellModelId);
            const cellModelName = cellModel?.name || "";
            const targetInstance = parameterInstances.find(({ targetId }) => targetId === cellModelId);
            const variantParameters = isNotEmpty(variant?.id) ? parametersForVariants?.[variant?.id ?? 0] : [];
            const variantParameter = variantParameters?.find((item) => item?.parameterName === name);
            const value = variantParameter ? variantParameter?.parameterValue : targetInstance?.value;
            return targetInstance
                ? { ...targetInstance, value, adjustmentId: variant?.id }
                : {
                    ...getEmptyParameterInstance(),
                    targetId: cellModelId,
                    targetName: cellModelName,
                };
        }
        case ResultElementParameterType.RECIPE: {
            const recipe = recipes.find(({ id }) => id === recipeId);
            const recipeName = recipe?.name || "";
            const targetInstance = parameterInstances.find(({ targetId }) => targetId === recipeId);
            const variantParameters = isNotEmpty(variant?.id) ? parametersForVariants?.[variant?.id ?? 0] : [];
            const variantParameter = variantParameters?.find((item) => item?.parameterName === name);
            const value = variantParameter ? variantParameter?.parameterValue : targetInstance?.value;
            return targetInstance
                ? { ...targetInstance, value, adjustmentId: variant?.id }
                : {
                    ...getEmptyParameterInstance(),
                    targetId: recipeId,
                    targetName: recipeName,
                };
        }
        case ResultElementParameterType.ALTERNATIVE_FLOW: {
            const recipe = recipes.find(({ id }) => id === recipeId);
            const recipeName = recipe?.name || "";
            const usedStep = (flatSteps(recipe?.state.steps ?? []).find((step) => step.type === RecipeStepType.ALTERNATIVE && step.name === name) || {});
            const overridenFlowInVariant = variant?.flowConfig?.stepsConfig?.find((flow) => flow.stepId === usedStep?.id)?.flowId;
            const flowId = isNotEmpty(overridenFlowInVariant)
                ? overridenFlowInVariant
                : (usedStep.flowProperties || []).find(({ isDefault }) => isDefault)?.id;
            const value = getFlowNameById(usedStep.flowProperties || [], flowId);
            return {
                ...getEmptyParameterInstance(),
                ...(isNotEmpty(value, true) ? { value } : {}),
                adjustmentId: variant?.id,
                targetId: recipeId,
                targetName: recipeName,
            };
        }
    }
};
export const getResultElementParameterValues = (parameter, sources, cellModels, recipes, simulations, insights) => {
    if (parameter === null || !isNotEmpty(parameter?.parameter?.type)) {
        return [];
    }
    let parameterInstances = [];
    const { name, type } = parameter?.parameter ?? {};
    switch (type) {
        case ResultElementParameterType.CELL_MODEL:
            parameterInstances = getCellModelParameterValues(cellModels, name);
            break;
        case ResultElementParameterType.RECIPE: {
            const { units } = (parameter?.parameter ?? {});
            parameterInstances = getRecipeParameterValues(recipes, name).map((parameterInstance) => ({
                ...parameterInstance,
                ...(parameterInstance.units === units
                    ? {}
                    : {
                        value: convertValueBasedOnUnits(Number(parameterInstance.value), parameterInstance.units, units),
                        units: units,
                    }),
            }));
            break;
        }
        case ResultElementParameterType.ALTERNATIVE_FLOW:
            parameterInstances = getRecipeAlternativeStepFlows(recipes, name);
            break;
    }
    if (sources.filter((item) => !!item && !!item.type).length === 0) {
        return parameterInstances;
    }
    const highlightedParameterInstances = sources
        .map(mapSpaceViewerDataToSourceTemplate)
        .reduce((acc, source) => {
        switch (source?.type) {
            case ResultElementSourceType.Simulation: {
                const { simulationId, adjustmentId } = source;
                const parameterInstance = getParameterInstanceFromSimulation(parameter, parameterInstances, simulations, cellModels, recipes, simulationId, adjustmentId ?? undefined);
                const existedInstanceIndex = acc.findIndex(({ targetId, adjustmentId }) => targetId === parameterInstance?.targetId && adjustmentId === parameterInstance?.adjustmentId);
                return existedInstanceIndex === -1 && !!parameterInstance
                    ? [...acc, { ...parameterInstance, highlighted: !!source.view }]
                    : acc;
            }
            case ResultElementSourceType.Insight: {
                const { inputSetId, insightId, outputId } = source;
                const isFinal = source?.isFinal || outputId === -1;
                const { state: { input: { inputSets = [] } = {}, outputs = [] } = {} } = insights.find(({ id }) => insightId === id) || {};
                const { inputs = [] } = inputSets.find(({ id }) => id === inputSetId) || {};
                let selectedInputs = inputs;
                if (!isFinal && isNotEmpty(outputId)) {
                    const { sourceA, sourceB } = outputs.find(({ id }) => id === outputId) || {};
                    selectedInputs = inputs.filter(({ id }) => id === sourceA || id === sourceB);
                }
                selectedInputs.forEach((input) => {
                    if (input.inputType !== InsightInputType.SIMULATION) {
                        return;
                    }
                    const { inputId: simulationId, adjustmentId } = input;
                    const parameterInstance = getParameterInstanceFromSimulation(parameter, parameterInstances, simulations, cellModels, recipes, simulationId ?? undefined, adjustmentId ?? undefined);
                    const existedInstanceIndex = acc.findIndex(({ targetId, adjustmentId }) => targetId === parameterInstance?.targetId && adjustmentId === parameterInstance?.adjustmentId);
                    if (existedInstanceIndex === -1 && !!parameterInstance) {
                        acc.push({ ...parameterInstance, highlighted: !!source.view });
                    }
                });
                return acc;
            }
        }
        return acc;
    }, [])
        .sort((_, instance2) => (instance2.highlighted ? 1 : -1));
    const notEmptyInstances = highlightedParameterInstances.filter(({ value }) => value !== N_A && isNotEmpty(value));
    return notEmptyInstances.length === 0 ? [getEmptyParameterInstance()] : highlightedParameterInstances;
};
export const isOptimizationPreviewRequestOnlyTemporary = (optimizationPreviews) => {
    const temporaryPreviewRequestId = optimizationPreviews.temporary?.requestId;
    for (const persistedPreview of Object.values(optimizationPreviews.persisted)) {
        if (persistedPreview.requestId === temporaryPreviewRequestId) {
            return false;
        }
    }
    return true;
};
export const getSelectedParameterToOptimize = (parameter, target, targetType, parametersOverrides) => {
    if (!isNotEmpty(target?.state?.parameters)) {
        return;
    }
    switch (parameter?.type) {
        case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE:
            return targetType === OptimizationTargetType.RECIPE
                ? getSelectedRecipeParameterToOptimize(target, parametersOverrides, parameter?.parameterId)
                : getSelectedCellModelParameterToOptimize(target, parametersOverrides, parameter?.parameterId);
        case OptimizationParameterParameterType.ALTERNATIVE_FLOW:
            return getSelectedRecipeAlternativeStepToOptimize(target, parameter?.stepId);
    }
};
export const getOptimizationParameterSectionTargetSource = (selectedInput, workspaceSimulations) => {
    if (selectedInput?.inputType !== InsightInputType.SIMULATION) {
        return;
    }
    const selectedSimulation = workspaceSimulations?.find(({ id }) => id === selectedInput?.inputId);
    return selectedSimulation !== undefined
        ? {
            source: selectedSimulation,
            sourceId: selectedSimulation.id,
            adjustmentId: selectedInput.adjustmentId ?? 0,
        }
        : undefined;
};
export const getOptimizationParameterSectionTarget = (recipes, cellModels, targetSource, targetType) => {
    switch (targetType) {
        case OptimizationTargetType.CELL_MODEL:
            return cellModels.find(({ id }) => targetSource?.source.cellModelId === id);
        case OptimizationTargetType.RECIPE:
            return recipes.find(({ id }) => targetSource?.source.recipeId === id);
    }
};
export const getResultElementInitialName = (name, elements, duplicateTargetResultSetId) => {
    if (!isNotEmpty(duplicateTargetResultSetId)) {
        return name || "";
    }
    let i = 0;
    while (i <= MAX_RESULT_ELEMENTS) {
        const elementName = `Copy of ${name ?? ""}${i === 0 ? "" : ` ${i}`}`;
        if (!elements.find(({ name }) => elementName === name)) {
            return elementName;
        }
        i++;
    }
    return "";
};
